import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Form, FormGroup, Label, Input, Button, Alert } from "reactstrap";
import Header from "../components/public/Header";
import Footer from "../components/public/Footer";
import OtpInput from "../components/public/OtpInput";
import AuthService from "../services/authService";
import UserContext from "../contexts/UserContext";
import TokenService from "../services/tokenService";
import styles from "../styles/public/otp-login.module.css";
import api from "../services/api";

const OtpLoginPage = () => {
  const [email, setEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [expiresIn, setExpiresIn] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [otpKey, setOtpKey] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useContext(UserContext);

  const from = location.state?.from || "/league/upcoming";
  const isBookingFlow = location.state?.isBookingFlow || false;

  useEffect(() => {
    const currentUser = AuthService.getCurrentUser();
    const token = currentUser?.access;

    if (currentUser?.isCustomer && token && TokenService.isValidToken(token)) {
      navigate(from, { replace: true });
    }
  }, [from, navigate]);

  useEffect(() => {
    api.get("/get-org-details/")
      .then((response) => {
        setOrganization(response.data);
      });
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const response = await AuthService.requestOtp(email);
      setOtpSent(true);
      setExpiresIn(response.data.expires_in || 600);
      setSuccess(`Verification code sent to ${email}! Check your email.`);
      setResendCooldown(60); // 60 second cooldown
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to send verification code. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (code) => {
    setError("");
    setLoading(true);

    try {
      const response = await AuthService.verifyOtp(email, code);
      const accessToken = response.access || response.token;

      // Set user context with customer data
      setUser({
        username: email,
        organization: null,
        isOwner: false,
        isCustomer: true,
        email: email,
        token: accessToken,
        expiresAt: response.expires_at,
      });

      setSuccess("Successfully logged in!");

      // Redirect after brief success message
      setTimeout(() => {
        navigate(from, { replace: true });
      }, 1000);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Invalid or expired code. Please try again.";
      setError(errorMessage);
      setOtpKey(prev => prev + 1); // Clear OTP input on error
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;

    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await AuthService.requestOtp(email);
      setExpiresIn(response.data.expires_in || 600);
      setSuccess("New verification code sent!");
      setResendCooldown(60);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to resend code. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = () => {
    setOtpSent(false);
    setError("");
    setSuccess("");
    setExpiresIn(null);
  };

  const handleSkipLogin = () => {
    navigate("/league/upcoming", { replace: true });
  };

  return (
    <>
      <Header organization={organization} showBanner={false} />
      <Container className={styles["login-container"]}>
        <div className={styles["login-card"]}>
          <h2 className={styles["login-title"]}>Login</h2>
          <p className={styles["login-subtitle"]}>
            {otpSent
              ? "Enter the verification code sent to your email"
              : "Enter your email address to receive a verification code"}
          </p>

          {error && <Alert color="danger" fade timeout={150}>{error}</Alert>}
          {success && <Alert color="success" fade timeout={150}>{success}</Alert>}

          {!otpSent ? (
            <Form onSubmit={handleRequestOtp}>
              <FormGroup>
                <Label for="email" className={styles["form-label"]}>
                  Email Address
                </Label>
                <Input
                  type="email"
                  name="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  required
                  disabled={loading}
                  className={styles["form-input"]}
                />
              </FormGroup>

              <Button
                block
                disabled={loading || !email}
                className={styles["submit-button"]}
                style={{
                  backgroundColor: '#3c6e51',
                  fill: '#3c6e51',
                  color: '#ffffff',
                  fontWeight: 'normal',
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Sending...
                  </>
                ) : (
                  "Send Verification Code"
                )}
              </Button>

              {isBookingFlow && (
                <div className={styles["skip-section"]}>
                  <Button
                    color="link"
                    onClick={handleSkipLogin}
                    className={styles["skip-button"]}
                  >
                    Continue as guest
                  </Button>
                </div>
              )}
            </Form>
          ) : (
            <div className={styles["otp-section"]}>
              <div className={styles["otp-info"]}>
                <p className={styles["sent-to"]}>
                  Code sent to: <strong>{email}</strong>
                  {expiresIn && (
                    <span className={styles["expires-info"]}>
                      {" "}(expires in {Math.floor(expiresIn / 60)} minutes)
                    </span>
                  )}
                </p>
                <Button
                  color="link"
                  size="sm"
                  onClick={handleChangeEmail}
                  className={styles["change-button"]}
                >
                  Change
                </Button>
              </div>

              <OtpInput
                key={otpKey}
                length={6}
                onComplete={handleVerifyOtp}
                disabled={loading}
              />

              <div className={styles["resend-section"]}>
                {resendCooldown > 0 ? (
                  <p className={styles["cooldown-text"]}>
                    Resend code in {resendCooldown}s
                  </p>
                ) : (
                  <Button
                    color="link"
                    onClick={handleResendOtp}
                    disabled={loading}
                    className={styles["resend-button"]}
                  >
                    Resend Code
                  </Button>
                )}
              </div>

              {loading && (
                <div className="text-center mt-3">
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Verifying...
                </div>
              )}
            </div>
          )}
        </div>
      </Container>
      <Footer />
    </>
  );
};

export default OtpLoginPage;
